import * as Sentry from "@sentry/react";
import { isExpectedPaymentFailure } from "../utils/expectedPaymentFailure";


// Duck-type check: .stack is optional since some SDK errors (e.g. PayMe) only expose .message
const isErrorObject = (obj) => obj && typeof obj.message === 'string' && obj.message

/* Provider decline reasons are free text we do not control: PayMe's wording varies by language
   and some messages embed the seller's support address. Strip anything email-shaped and cap the
   length before it reaches Sentry. */
const redactForBreadcrumb = (reason) =>
    typeof reason === 'string' && reason.trim()
        ? reason.replace(/\S+@\S+\.\S+/g, '[email]').slice(0, 120)
        : undefined
const tryToSafelyStringify = (obj) => {
    try {
        return JSON.stringify(obj)
    } catch (e) {
        return String(obj);
    }
}

export const ErrorService = {
    handleError(e, hint) {
        try {
            /* Ensure e is always a proper Error so Sentry can extract a meaningful title.
               Use duck-typing instead of instanceof to handle cross-realm errors (e.g. iframes).
               Guard against e.message being a non-string (e.g. PayMe plain objects). */
            const err = isErrorObject(e)
                ? e
                : new Error(typeof e?.message === 'string' && e.message ? e.message : tryToSafelyStringify(e) || 'Unknown error');

            /* Report to Sentry with optional context from hint.data */
            if (hint?.data) {
                Sentry.withScope((scope) => {
                    // Add structured tags for filtering in Sentry
                    Object.entries(hint.data).forEach(([key, value]) => {
                        if (value !== undefined && value !== null && typeof value !== 'object') {
                            scope.setTag(key, String(value))
                        } else if (value && typeof value === 'object') {
                            // Objects go as extra data (visible in Sentry "Additional Data")
                            scope.setExtra(key, value)
                        }
                    })
                    Sentry.captureException(err, hint)
                })
            } else {
                Sentry.captureException(err, hint)
            }
        } catch (veryBadError) {
            console.error(veryBadError);
        }
    },

    /* Same as handleError, but drops failures that are the buyer's card being declined or a
       business outcome we already show them (sold out, slot full). Those are expected results
       of a checkout, not defects, and reporting them buried the real payment crashes.

       Dropped failures still leave a breadcrumb, so if a genuine error follows in the same
       session the declines that preceded it are visible as context — at no quota cost. */
    /* `rawError` is the provider's own object, which is what the predicate must inspect.
       `errorToReport` is the presentable Error the caller built for Sentry's title/grouping —
       defaults to rawError when the caller has nothing better. */
    handlePaymentError(rawError, hint, errorToReport) {
        const e = rawError;
        try {
            if (isExpectedPaymentFailure(e)) {
                Sentry.addBreadcrumb({
                    category: 'payment',
                    level: 'info',
                    /* The reason is provider-controlled free text and can embed the seller's own
                       support address, so it is sanitised rather than interpolated verbatim:
                       emails redacted, length capped. Kept at all because "a decline happened"
                       without "why" is near-useless as context for a later crash. */
                    message: 'Expected payment failure',
                    data: {
                        paymeErrorType: e?.type,
                        paymeErrorCode: e?.code,
                        backendErrorCode: e?.details?.data?.error ?? e?.response?.data?.error ?? e?.data?.error,
                        declineReason: redactForBreadcrumb(e?.payload?.status_error_details),
                    },
                });
                return;
            }
        } catch (predicateError) {
            /* Never let the filter itself swallow a payment error — fall through and report. */
            console.error(predicateError);
        }
        this.handleError(errorToReport === undefined ? rawError : errorToReport, hint);
    }
}